import { readDocumentLocale } from '@agnivo/app-core';
import type { AppConfig, OidcAuthConfig } from '@agnivo/app-core';

export type AuthMode = 'mock' | 'oidc';

/**
 * Read a build-time override, falling back to the dev default — where UNSET and
 * EMPTY both mean "not configured".
 *
 * `??` alone was not enough, and the dev images proved it (2026-08-17): Docker
 * injects every declared ARG into subsequent RUN steps as an ENV VAR, including
 * the four prod-override ARGs whose DEFAULTS are empty strings. The Dockerfile's
 * `${VAR:+…}` conditionals omit the re-declaration for empty values but cannot
 * remove what the ARG already placed in the environment — so the Vite build saw
 * `VITE_OIDC_ISSUER=""` (etc.), `""` is not nullish, and every dev image shipped
 * `apiBaseUrl:""` plus a UserManager with no authority. Sign-in on the deployed
 * dev surface was a silent no-op until this guard. The Dockerfile documents the
 * leak on its RUN line; this is the enforcement, so the surface stays correct
 * even under build recipes that re-introduce it.
 */
function envOrDefault(value: string | undefined, devDefault: string): string {
  return value === undefined || value === '' ? devDefault : value;
}

/**
 * web-ui picks its auth provider: mock (zero-backend default) or Zitadel OIDC PKCE.
 *
 * ⭐ Declared BEFORE `appConfig`, not just above `oidcConfig` where it used to sit —
 * `routeViewAnalytics()` below reads this same const rather than re-deriving it, and
 * `appConfig`'s object literal calls that function eagerly as one of its own
 * properties. A `const` declared after the point it is read throws at module-eval
 * time (temporal dead zone), so the ordering here is load-bearing, not cosmetic.
 */
export const authMode = (import.meta.env.VITE_AUTH_MODE ?? 'mock') as AuthMode;

/**
 * web-ui's runtime config, read from its OWN Vite env (app-core can't read
 * import.meta.env — it ships compiled; the shell injects config, ADR-079).
 * Defaults keep the app fully functional with zero backend (mock transport +
 * mock auth) until the public /v1 contract + browser OIDC client land.
 */
export const appConfig: AppConfig = {
  apiMode: (import.meta.env.VITE_API_MODE ?? 'mock') as AppConfig['apiMode'],
  /** The reference web surface — thread provenance is web-family. */
  surface: 'web',
  // ADR-141 D1.2 analytics identity — set explicitly, never derived from `surface`.
  clientShell: 'web-ui',
  /** ADR-072 domain map: api.dev.agnivo.org (dev) / api.agnivo.org (prod). */
  apiBaseUrl: envOrDefault(import.meta.env.VITE_API_BASE_URL as string | undefined, 'https://api.dev.agnivo.org'),
  /**
   * ADR-092 flip (choreography step: after the gateway legs + DS flag are
   * live on staging — developer ping 2026-07-18): carry the addressed
   * agent_id on send/:stream/reads. Prod carry-condition: the ADR-012
   * cutover checklist must include "gateway ADR-092 legs promoted" before
   * prod CLIENT_REST exposure (mini-app is dark on prod until then).
   */
  sendAgentId: true,
  /**
   * Voice P2.2 (ADR-094) capture — ON for the reference web surface.
   *
   * THIS FLAG'S ABSENCE WAS A GAP, NOT A DECISION, and the distinction is the
   * reason this comment is long. `voiceCapture` defaults OFF in `AppConfig`, so a
   * surface that never names it renders a plain send button and no microphone —
   * indistinguishable, from the outside, from a surface that deliberately opted
   * out. web-ui was in the first category and read as the second for a month.
   *
   * Found the only way it could be: Dmitry was invited to record a voice message
   * here on 2026-08-21 and reported there was no button (screenshot, DM 460). The
   * invite was mine, and the check behind it was wrong in an instructive way — I
   * verified that all four shells mount app-core's shared `routes` and recorded
   * that as "the surface is ready". That is true about the SCREEN and says nothing
   * about the AFFORDANCE: routing decides which components exist, a per-shell
   * capability flag decides whether this one renders. Two different seams; I
   * checked one and reported the other.
   *
   * WHY ON IS SAFE HERE, given the flag's default-OFF rationale is a live wire.
   * The `AppConfig` docblock gates this on "developer's 2.1 gateway audio upload
   * endpoint is live and ASR wired" — otherwise the button's sends have nowhere to
   * go. That gate is closed, and mini-app is the proof: it has run `voiceCapture:
   * true` since c045175, and `ChatScreen`'s `sendVoice` path has no per-surface
   * branch — same component, same `api-client`, same gateway leg. web-ui differs
   * from the proven case only in being an ordinary browser.
   *
   * That direction matters. The one recorded doubt about capture was the
   * 2026-07-21 "WebView records unplayable blobs" diagnosis — which was WRONG and
   * was reversed the same evening on device ground truth (the decode probe: real
   * signal, `peak 0.068` on Android). The actual bug was `<audio src=blob:>` in
   * Telegram's WebView, fixed by the Web-Audio `VoiceClipPlayer` (8287543). So the
   * hostile environment already works, and web-ui is the easier one — Chrome on
   * Android supports `MediaRecorder` natively.
   *
   * No `voiceFallbackUrl` alongside it, unlike mini-app. That hint is the honest
   * degrade for a surface where capture is UNAVAILABLE; the Composer still renders
   * it if `MediaRecorder` is missing at runtime, and pointing a working surface at
   * the bot chat would send people away from the thing they can do right here.
   */
  voiceCapture: true,
  /**
   * Two-thread v1 (§2.1/§2.3) — the ONE flag in this file that does not carry a
   * dev default, and the `=== 'true'` is the whole point.
   *
   * Every sibling above uses `?? <dev value>`: unset means "you did not configure
   * this, have the dev one". Here unset means OFF. A rollout flag that defaults ON
   * ships unreleased UI silently — no red, no 404, nothing to notice — whereas a
   * mis-set `apiBaseUrl` announces itself on the first call. Different failure
   * directions, so they get different defaults.
   *
   * That means the flag is off for `pnpm build`, off in the plain `dist` that
   * test:web-ui builds, and off for any image recipe that does not opt in. The dev
   * image opts in via `ARG VITE_TWO_THREAD_UI=true` in deploy/Dockerfile — read its
   * comment before changing either side; they are one decision written twice.
   *
   * Note `=== 'true'` and not `Boolean(...)`: env vars are strings, so the string
   * `"false"` is truthy and would turn the feature ON while reading as off.
   */
  twoThreadUi: import.meta.env.VITE_TWO_THREAD_UI === 'true',
  /**
   * 025 S7 goal view — same no-dev-default, `=== 'true'`, unset-means-OFF shape as
   * `twoThreadUi` above, for the same reason (see `AppConfig.goalsEnabled`'s docblock in
   * `packages/app-core/src/config.ts`: this gates a CALL that cannot otherwise tell "surface
   * dark" from "no goal yet" apart).
   *
   * `deploy/Dockerfile` opts the DEV image in (`ARG VITE_GOALS_ENABLED=true`); the prod build
   * must pass `false` explicitly — enforced by `scripts/assert-web-ui-image-builds.mjs`.
   */
  goalsEnabled: import.meta.env.VITE_GOALS_ENABLED === 'true',
  /**
   * ADR-156 widget-config page (`/widgets`, DEV ONLY for now — product decision
   * `decisions/product/2026-10-05-widget-config-page-remount-dev-behind-flag.md`). Same
   * no-dev-default, `=== 'true'`, unset-means-OFF shape as `goalsEnabled` above: the page is a
   * dead end wherever the gateway's `WIDGET_ADMIN_REST_ENABLED` is off. `deploy/Dockerfile` opts
   * the DEV image in; a prod build must pass `false` explicitly (`prodRequiredArgs` in
   * `scripts/assert-web-ui-image-builds.mjs`). Read by `App.tsx` to decide whether the route
   * exists at all.
   */
  widgetConfigEnabled: import.meta.env.VITE_WIDGET_CONFIG_ENABLED === 'true',
  /**
   * Emit THE LOCALE THIS SURFACE IS RENDERING on every live `/v1` call. The gateway's
   * `refusalcopy.Render(reason, planID, locale, …)` reads `locale` from inbound
   * `x-language-code` metadata and renders the matching RU/EN copy. Both consumers
   * need it: the chat ApiProvider AND the admin-tenant section's AdminApiProvider
   * (migrated 2026-08-18), whose refusal copy is locale-rendered server-side.
   *
   * ⛔ **This was the literal `'ru'` until 2026-09-02, and it was wrong on THIS surface
   * specifically.** web-ui's copy is detection-driven — `getMessages()` in app-core
   * defaults to `detectLocale()`, and the embedded admin section follows the same
   * decision through its own `surfaceLocale()` — so an `en-*` browser renders English
   * under `<html lang="en">` and was asking the gateway for Russian. Server-rendered
   * refusal sentences came back in Russian inside an English page. That is product's
   * agreement invariant (`decisions/product/2026-08-30-document-lang-mirrors-ui-locale.md`)
   * on a third contract: `lang`, the copy, and the locale we ASK FOR are one decision.
   *
   * ⚠️ The old comment justified the literal with *"no runtime locale selector yet —
   * getMessages() defaults to 'ru'"*. That justification was already false when it was
   * read: app-core's `getMessages` defaults to `detectLocale()`, not to `'ru'`. The
   * hardcoded value was harmless while the gateway dropped the header, and stopped being
   * harmless in `gateway-service@c4c758b` (2026-08-17T00:12Z), which added
   * `PresentationHeaders` and made `presentationHeaderMatcher` forward it — see
   * `lib/saveRefusal.ts`, whose docblock recorded the old drop and is corrected in the
   * same MR.
   *
   * ⛔ **A COMMIT, not a date, and do NOT "correct" this back to 2026-08-16.** The
   * architect's presentation-header ruling IS dated 2026-08-16, and `gateway-service`'s
   * own `cors.go` says so in two places — so 08-16 is the date every nearby source
   * offers, and it is the wrong one for THIS sentence. The forwarding began when the
   * code landed: `c4c758b`, twelve minutes past midnight UTC on 08-17, first appearance
   * of `PresentationHeaders` (`git log -S` in that repo). Ruling date and behaviour date
   * are different facts about different artifacts, and product's review of `web!540`
   * caught me having merged them.
   *
   * ⭐ Which is why this now names a SHA. Every claim in this file about another
   * service's behaviour is pinned to the commit that makes it true, because a date can
   * be plausibly wrong in a way a SHA cannot — and because the thing this whole comment
   * exists to record is a cross-repo fact that rotted silently once already.
   *
   * ⭐ `readDocumentLocale()`, NOT `detectLocale()`: `main.tsx` already made this
   * decision and wrote it to `<html lang>` before `createRoot`. Re-running the resolver
   * here would be a second decision on a per-request path — the same argument the
   * admin section's `surfaceLocale()` makes for a nested dictionary.
   *
   * The callback is called per request, so a LocaleContext/runtime switch lands without
   * rebuilding the client — and, reading the document rather than a captured value, a
   * switch that updates `<html lang>` is picked up here for free.
   */
  requestHeaders: () => ({ 'x-language-code': readDocumentLocale() }),
  /**
   * ADR-141 D1.1 — the Umami collector, and the switch that arms the route-view
   * sender on this surface. `undefined` unless BOTH values are configured.
   *
   * ⛔ NO DEV DEFAULT, unlike every other field above, and the asymmetry is the
   * point. `envOrDefault` exists because a missing API base URL breaks the app
   * loudly and a dev default keeps it usable with zero backend. A missing
   * analytics collector breaks nothing — it just means no route views — so the
   * safe direction here is the opposite: a default would make a build that
   * nobody configured start POSTing to a guessed origin, and a wrong website id
   * writes rows into someone else's dataset where they are indistinguishable
   * from real ones. Missing-and-silent beats configured-and-wrong.
   *
   * ⚠️ The empty-string case is checked, not just the nullish one — same Docker
   * ARG leak `envOrDefault` documents above (2026-08-17): a declared ARG with an
   * empty default lands in the build env as `""`, which `??` accepts. Here that
   * would produce `websiteId: ''`, and Umami answers a bad website id with 200
   * while recording nothing — a silently dead funnel that looks wired.
   */
  analytics: routeViewAnalytics(),
};

function routeViewAnalytics(): AppConfig['analytics'] {
  const collectorUrl = import.meta.env.VITE_UMAMI_URL as string | undefined;
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;
  if (!collectorUrl || !websiteId) return undefined;
  // `authMode`'s own union ('mock' | 'oidc') is a subtype of the contract-derived
  // AuthModeToken ('mock' | 'oidc' | 'bridge' | 'telegram'), so this is a plain
  // assignment, not a cast — the day this app's own AuthMode widens past what the
  // contract declares, THIS line is where it fails to compile.
  //
  // `hostname` — architect ruling `decisions/architect/2026-09-16-umami-hostname-fix-bprime-null-shape-ruling.md`,
  // AMENDMENT 2026-09-18T08:50Z: this shell's own deployed host, as a build-time constant. The dev
  // default is web-ui's actual dev host (`app.dev.agnivo.org`, per ADR-072); a prod build overrides
  // via `VITE_APP_HOSTNAME=app.agnivo.org`. Both are in `infra`'s `umami-schema-pin-guard` manifest's
  // `allowed:` set for `website_event.hostname` — do not send anything outside that set.
  const hostname = envOrDefault(import.meta.env.VITE_APP_HOSTNAME as string | undefined, 'app.dev.agnivo.org');
  return { collectorUrl, websiteId, authMode, hostname };
}

/**
 * Zitadel SPA app for THIS surface (agnivo-platform-web; ADR-081 step 4).
 * Defaults are the DEV values — all public by design (public client, no
 * secret; SRE hand-off 2026-07-10). Prod values arrive with the ADR-012
 * cutover package and override via env at build time.
 */
export const oidcConfig: OidcAuthConfig = {
  issuer: envOrDefault(import.meta.env.VITE_OIDC_ISSUER as string | undefined, 'https://auth.dev.agnivo.org'),
  clientId: envOrDefault(import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined, '379037900588712699'),
  projectId: envOrDefault(import.meta.env.VITE_OIDC_PROJECT_ID as string | undefined, '371607459678126787'),
};

/**
 * Conversation Workspace (Init014 Release A) dev preview: mounts `/conversations` over a MOCK adapter.
 * Default OFF and deliberately NOT a Dockerfile ARG: an unset env is `false`, so a prod build cannot
 * carry the route or its chunk (the `import()` is dead code under a false constant). Turn on locally
 * with `VITE_CONVERSATIONS_PANEL=true`. Removed when the live adapter replaces the mock.
 */
export const conversationsPanelEnabled = import.meta.env.VITE_CONVERSATIONS_PANEL === 'true';

/**
 * Dialog deletion (061 lane 2, ADR-199 D13): the delete action in the conversations panel. Default OFF and
 * deliberately NOT a Dockerfile ARG, like the flag above. Mirrors the server's default-dark
 * `DIALOG_DELETION_REST_ENABLED` (gateway-service!577): flip this only after that flag is live in the same env,
 * or the action renders and every confirm fails closed with FAILED_PRECONDITION.
 */
export const dialogDeletionEnabled = import.meta.env.VITE_DIALOG_DELETION_ENABLED === 'true';

/**
 * Initiative 020: capture `src` / `click_id` from the register page URL. CAPTURE ONLY: nothing is persisted
 * or sent until the ADR-182 D2 amendment merges. Unset means OFF in every build (including prod), so unlike
 * `widgetConfigEnabled` no Dockerfile/prod-args wiring is needed to keep it dark. Kept out of `appConfig`
 * because that type lives in app-core, which every surface compiles.
 */
export const registerAttributionCapture = import.meta.env.VITE_REGISTER_ATTRIBUTION_CAPTURE === 'true';
